/**
 * Queue Page
 * 
 * Global queue view with paginated URL table and live monitoring.
 * Two tabs: Queue (table of all URLs) and Live Monitor (progress + logs).
 */

import { useState, useCallback } from "react";
import { Search, RefreshCw, ListOrdered, Activity } from "lucide-react";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { Badge } from "@repo/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/select";
import { QueueTable } from "@/components/queue/QueueTable";
import { LiveMonitor } from "@/components/queue/LiveMonitor";
import { useQueueRealtime } from "@/hooks/useQueueRealtime";
import type { QueueFilters } from "@/lib/api/queue";
import { cn } from "@repo/ui/lib/utils";

const Queue = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState("queue");

  const filters: QueueFilters = {
    status: statusFilter !== "all" ? statusFilter : undefined,
    search: searchQuery || undefined,
  };

  const {
    items,
    totalCount,
    stats,
    currentPage,
    totalPages,
    pageSize,
    isLoading,
    setCurrentPage,
    refetch,
  } = useQueueRealtime(filters);

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    refetch();
  }, [refetch]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Queue & Monitor</h1>
          <p className="text-muted-foreground">
            Track crawl queue progress and view live operation logs
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Status summary badges */}
          <div className="hidden md:flex items-center gap-1.5">
            {stats.pending > 0 && (
              <Badge variant="outline" className="bg-yellow-50 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800">
                {stats.pending} pending
              </Badge>
            )}
            {stats.in_progress > 0 && (
              <Badge variant="outline" className="bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800">
                {stats.in_progress} active
              </Badge>
            )}
            <Badge variant="outline">
              {stats.total.toLocaleString()} total
            </Badge>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Queued" value={stats.total} />
        <StatCard 
          label="Pending" 
          value={stats.pending} 
          className="text-yellow-600 dark:text-yellow-400" 
        />
        <StatCard 
          label="Completed" 
          value={stats.completed} 
          className="text-green-600 dark:text-green-400" 
        />
        <StatCard 
          label="Failed" 
          value={stats.failed} 
          className="text-red-600 dark:text-red-400" 
        />
      </div>

      {/* Main Content with Tabs */}
      <div className="card-elevated">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-4 border-b border-border flex items-center justify-between">
            <TabsList className="bg-transparent h-12 p-0 gap-4">
              <TabsTrigger 
                value="queue" 
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none px-1 gap-2"
              >
                <ListOrdered className="w-4 h-4" />
                Queue
                {totalCount > 0 && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    {totalCount.toLocaleString()}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="monitor"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none px-1 gap-2"
              >
                <Activity className="w-4 h-4" />
                Live Monitor
                {stats.in_progress > 0 && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {activeTab === "queue" && (
              <Button variant="ghost" size="sm" onClick={() => refetch()}>
                <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
              </Button>
            )}
          </div>

          <TabsContent value="queue" className="m-0">
            {/* Queue Filters */}
            <div className="p-4 border-b border-border">
              <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search URLs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </form>
            </div>

            {/* Queue Table */}
            <QueueTable
              items={items}
              totalCount={totalCount}
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              isLoading={isLoading}
              onPageChange={setCurrentPage}
              onRefresh={refetch}
            />
          </TabsContent>

          <TabsContent value="monitor" className="m-0 p-4">
            <LiveMonitor />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

function StatCard({ 
  label, 
  value, 
  className 
}: { 
  label: string; 
  value: number; 
  className?: string;
}) {
  return (
    <div className="card-elevated p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={cn("text-2xl font-bold mt-1", className)}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}

export default Queue;
